import { api } from "@/lib/api";
import {
  StaffWorkingHours,
  StaffWorkingHoursResponse,
  HistoryResponse,
  HistoryQueryParams,
  TimeTrackingReportsResponse,
  ReportsQueryParams,
} from "@/types/staffTimeTracking.types";

export const staffTimeTrackingService = {
  async startTracking(): Promise<StaffWorkingHours> {
    try {
      const response = await api.post<StaffWorkingHoursResponse>(
        "/staff/time/start",
        {}
      );
      if (!response.data.data) {
        throw new Error("No data returned from server");
      }
      return response.data.data;
    } catch (error: any) {
      console.error("Error starting time tracking:", error);
      throw error;
    }
  },

  async pauseTracking(): Promise<StaffWorkingHours> {
    try {
      const response = await api.post<StaffWorkingHoursResponse>(
        "/staff/time/pause",
        {}
      );
      if (!response.data.data) {
        throw new Error("No data returned from server");
      }
      return response.data.data;
    } catch (error: any) {
      console.error("Error pausing time tracking:", error);
      throw error;
    }
  },

  async resumeTracking(): Promise<StaffWorkingHours> {
    try {
      const response = await api.post<StaffWorkingHoursResponse>(
        "/staff/time/resume",
        {}
      );
      if (!response.data.data) {
        throw new Error("No data returned from server");
      }
      return response.data.data;
    } catch (error: any) {
      console.error("Error resuming time tracking:", error);
      throw error;
    }
  },

  async endTracking(): Promise<StaffWorkingHours> {
    try {
      const response = await api.post<StaffWorkingHoursResponse>(
        "/staff/time/end",
        {}
      );
      if (!response.data.data) {
        throw new Error("No data returned from server");
      }
      return response.data.data;
    } catch (error: any) {
      console.error("Error ending time tracking:", error);
      throw error;
    }
  },

  async getCurrentSession(): Promise<StaffWorkingHours | null> {
    try {
      const response = await api.get<StaffWorkingHoursResponse>(
        "/staff/time/current"
      );
      return response.data.data;
    } catch (error: any) {
      // If 404, return null (no active session)
      if (error.response?.status === 404) {
        return null;
      }
      console.error("Error fetching current session:", error);
      throw error;
    }
  },

  async getHistory(
    params?: HistoryQueryParams
  ): Promise<HistoryResponse["data"]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.userId) {
        queryParams.append("userId", params.userId);
      }
      if (params?.date) {
        queryParams.append("date", params.date);
      }
      if (params?.page) {
        queryParams.append("page", params.page.toString());
      }
      if (params?.limit) {
        queryParams.append("limit", params.limit.toString());
      }

      const queryString = queryParams.toString();
      const url = `/staff/time/history${queryString ? `?${queryString}` : ""}`;

      // Debug logging
      console.log("Service: getHistory called with params:", params);
      console.log("Service: Query string:", queryString);
      console.log("Service: Full URL:", url);

      const response = await api.get<HistoryResponse>(url);
      console.log("Service: Response received, records count:", response.data.data.records?.length);
      return response.data.data;
    } catch (error: any) {
      console.error("Error fetching history:", error);
      throw error;
    }
  },

  async getReports(
    params?: ReportsQueryParams
  ): Promise<TimeTrackingReportsResponse["data"]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.userId) {
        queryParams.append("userId", params.userId);
      }
      if (params?.startDate) {
        queryParams.append("startDate", params.startDate);
      }
      if (params?.endDate) {
        queryParams.append("endDate", params.endDate);
      }

      const queryString = queryParams.toString();
      const url = `/staff/time/reports${queryString ? `?${queryString}` : ""}`;

      // Debug logging
      console.log("Service: getReports called with params:", params);
      console.log("Service: Query string:", queryString);
      console.log("Service: Full URL:", url);

      const response = await api.get<TimeTrackingReportsResponse>(url);
      console.log("Service: Response received, reports count:", response.data.data?.length);
      return response.data.data;
    } catch (error: any) {
      console.error("Error fetching reports:", error);
      throw error;
    }
  },
};

